import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import JsonLd from './components/JsonLd';
import {
  SITE_URL,
  formatPageTitle,
  formatDescription,
  buildOrganizationSchema,
  buildWebSiteSchema,
  buildArticleSchema,
  buildSchemaGraph,
} from './utils/seo';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useDarkMode } from './DarkModeContext';
import Footer from './Footer';
import './App.css';

function parseTitle(markdown) {
  const firstLine = (markdown || '').split('\n')[0] || '';
  const match = firstLine.match(/^#\s+(.+)$/);
  return match ? match[1].trim() : 'Blog Post';
}

function parseBody(markdown) {
  const lines = (markdown || '').split('\n');
  const first = lines[0] || '';
  if (first.startsWith('# ')) {
    return lines.slice(1).join('\n').trim();
  }
  return markdown || '';
}

function getExcerpt(markdown, maxLen = 150) {
  const body = parseBody(markdown);
  const plain = body
    .replace(/#{1,6}\s+/g, '')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/\n+/g, ' ')
    .trim();
  if (plain.length <= maxLen) return plain;
  return plain.slice(0, maxLen).trim() + '…';
}

function brandifyMerra(text) {
  if (!text) return text;
  // Replace standalone 'Merra' with 'Merra AI' while preserving domains like 'Merra.ai'
  return text.replace(/\bMerra\b(?!\s*AI)(?!\.)/g, 'Merra AI');
}

const MANIFEST_URL = `${process.env.PUBLIC_URL || ''}/blog/manifest.json`;

function findPostBySlug(manifestList, slugParam) {
  const needle = String(slugParam || '').toLowerCase();
  if (!needle) return null;
  return (
    (manifestList || []).find((p) => {
      const urlSlug = String(p.urlSlug || p.slug || '').toLowerCase();
      const fileSlug = String(p.slug || '').toLowerCase();
      return urlSlug === needle || fileSlug === needle;
    }) || null
  );
}

function formatDate(isoDate) {
  if (!isoDate) return '';
  // Parse YYYY-MM-DD as local calendar date (avoid UTC midnight day-shift)
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(isoDate).trim());
  if (match) {
    const d = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  }
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function BlogPost() {
  const { slug: urlSlugParam } = useParams();
  const { darkMode } = useDarkMode();
  const [content, setContent] = useState(null);
  const [manifest, setManifest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const baseUrl = process.env.PUBLIC_URL || '';

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    setManifest(null);
    setContent(null);

    fetch(MANIFEST_URL)
      .then((res) => {
        if (!res.ok) throw new Error('Manifest not found');
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        const manifestList = Array.isArray(data) ? data : [];
        const post = findPostBySlug(manifestList, urlSlugParam);
        if (!post) {
          setError(true);
          setLoading(false);
          return;
        }
        setManifest(manifestList);
        return fetch(`${baseUrl}/blog/${post.slug}.md`).then((res) => {
          if (!res.ok) throw new Error('Not found');
          return res.text();
        });
      })
      .then((text) => {
        if (cancelled) return;
        if (text !== undefined) {
          setContent(brandifyMerra(text));
        }
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [urlSlugParam, baseUrl]);

  const linkStyle = {
    color: darkMode ? '#4A9EFF' : '#1976d2',
    textDecoration: 'none',
  };

  const slugToUrlSlug = manifest
    ? Object.fromEntries(
        (manifest || []).flatMap((p) => {
          const dest = p.urlSlug || p.slug;
          return [
            [p.slug, dest],
            [String(p.slug).toLowerCase(), dest],
          ];
        })
      )
    : {};

  const renderLink = ({ href, children, ...props }) => {
    const isSameSitePath = href && href.startsWith('/') && !href.startsWith('//');
    const isInternal =
      href &&
      (href.startsWith('./') ||
        href.startsWith('/blog/') ||
        isSameSitePath ||
        (href.endsWith('.md') && !href.includes('://')));
    let to = href;
    if (href && href.endsWith('.md')) {
      const fileSlug = href.replace(/^\.\//, '').replace(/\.md$/, '');
      const prettySlug = slugToUrlSlug[fileSlug] ?? slugToUrlSlug[fileSlug.toLowerCase()] ?? fileSlug;
      to = `/blog/${prettySlug}`;
    } else if (href && href.startsWith('./') && !href.endsWith('.md')) {
      const fileSlug = href.replace(/^\.\//, '');
      const prettySlug = slugToUrlSlug[fileSlug] ?? slugToUrlSlug[fileSlug.toLowerCase()] ?? fileSlug;
      to = `/blog/${prettySlug}`;
    } else if (href && (href.startsWith('/blog/') || isSameSitePath)) {
      to = href;
    }
    if (isInternal && to) {
      return (
        <Link to={to} style={linkStyle} {...props}>
          {children}
        </Link>
      );
    }
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" style={linkStyle} {...props}>
        {children}
      </a>
    );
  };

  const sharedStyles = {
    article: {
      maxWidth: '900px',
      margin: '0 auto',
      padding: '40px 20px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", Arial, sans-serif',
      lineHeight: '1.8',
      color: darkMode ? '#e0e0e0' : '#333',
      backgroundColor: darkMode ? '#1a1a1a' : 'white',
      minHeight: '100vh',
    },
    backNav: { marginBottom: '30px' },
    h1: {
      fontFamily: "'Onest', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      color: darkMode ? '#ffffff' : '#2c3e50',
      fontSize: '42px',
      fontWeight: 'bold',
      lineHeight: '1.2',
      marginBottom: '20px',
    },
    h2: {
      fontFamily: "'Onest', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      fontSize: '32px',
      fontWeight: 'bold',
      color: darkMode ? '#ffffff' : '#2c3e50',
      marginTop: '50px',
      marginBottom: '30px',
      borderTop: `2px solid #AB5898`,
      paddingTop: '30px',
    },
    h3: {
      fontFamily: "'Onest', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      fontSize: '24px',
      fontWeight: 'bold',
      color: darkMode ? '#e0e0e0' : '#34495e',
      marginTop: '28px',
      marginBottom: '16px',
    },
    h4: { fontFamily: "'Onest', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", fontSize: '18px', fontWeight: 'bold', color: darkMode ? '#e0e0e0' : '#34495e', marginTop: '20px', marginBottom: '12px' },
    h5: { fontFamily: "'Onest', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", fontSize: '16px', fontWeight: 'bold', color: darkMode ? '#e0e0e0' : '#34495e', marginTop: '16px', marginBottom: '8px' },
    h6: { fontFamily: "'Onest', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", fontSize: '14px', fontWeight: 'bold', color: darkMode ? '#b0b0b0' : '#666', marginTop: '12px', marginBottom: '8px' },
    p: { marginBottom: '25px', color: darkMode ? '#e0e0e0' : '#333' },
    ul: {
      marginBottom: '30px',
      paddingLeft: '30px',
      color: darkMode ? '#e0e0e0' : '#333',
    },
    li: { marginBottom: '15px' },
    strong: { color: darkMode ? '#ffffff' : '#2c3e50', fontWeight: 'bold' },
  };

  const mdComponents = {
    a: renderLink,
    h1: ({ children }) => null,
    h2: ({ children }) => <h2 style={sharedStyles.h2}>{children}</h2>,
    h3: ({ children }) => <h3 style={sharedStyles.h3}>{children}</h3>,
    h4: ({ children }) => <h4 style={sharedStyles.h4}>{children}</h4>,
    h5: ({ children }) => <h5 style={sharedStyles.h5}>{children}</h5>,
    h6: ({ children }) => <h6 style={sharedStyles.h6}>{children}</h6>,
    p: ({ children }) => <p style={sharedStyles.p}>{children}</p>,
    ul: ({ children }) => <ul style={sharedStyles.ul}>{children}</ul>,
    ol: ({ children }) => <ol style={{ ...sharedStyles.ul, listStyle: 'decimal' }}>{children}</ol>,
    li: ({ children }) => <li style={sharedStyles.li}>{children}</li>,
    strong: ({ children }) => <strong style={sharedStyles.strong}>{children}</strong>,
    blockquote: ({ children }) => (
      <blockquote
        style={{
          marginBottom: '25px',
          paddingLeft: '20px',
          borderLeft: `4px solid ${darkMode ? '#AB5898' : '#AB5898'}`,
          color: darkMode ? '#b0b0b0' : '#666',
          fontStyle: 'italic',
        }}
      >
        {children}
      </blockquote>
    ),
    code: ({ children, className }) => {
      const isBlock = className != null;
      if (isBlock) {
        return (
          <pre
            style={{
              overflow: 'auto',
              padding: '16px',
              borderRadius: '8px',
              backgroundColor: darkMode ? '#2a2a2a' : '#f5f5f5',
              marginBottom: '25px',
              fontSize: '14px',
            }}
          >
            <code>{children}</code>
          </pre>
        );
      }
      return (
        <code
          style={{
            backgroundColor: darkMode ? '#2a2a2a' : '#f0f0f0',
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '0.9em',
          }}
        >
          {children}
        </code>
      );
    },
  };

  if (loading) {
    return (
      <div style={sharedStyles.article}>
        <div style={sharedStyles.backNav}>
          <Link to="/" style={linkStyle}>← Back to Home</Link>
          <span style={{ margin: '0 8px', color: darkMode ? '#666' : '#999' }}>|</span>
          <Link to="/blog" style={linkStyle}>Blog</Link>
        </div>
        <p style={{ color: darkMode ? '#b0b0b0' : '#666' }}>Loading…</p>
        <Footer />
      </div>
    );
  }

  if (error || !content) {
    return (
      <div style={sharedStyles.article}>
        <div style={sharedStyles.backNav}>
          <Link to="/" style={linkStyle}>← Back to Home</Link>
          <span style={{ margin: '0 8px', color: darkMode ? '#666' : '#999' }}>|</span>
          <Link to="/blog" style={linkStyle}>Blog</Link>
        </div>
        <h1 style={sharedStyles.h1}>Article not found</h1>
        <p style={sharedStyles.p}>
          This post may have been moved or the link might be incorrect.
        </p>
        <Link to="/blog" style={linkStyle}>← Back to Blog</Link>
        <Footer />
      </div>
    );
  }

  const title = parseTitle(content);
  const body = parseBody(content);
  const postMeta = findPostBySlug(manifest, urlSlugParam);
  const description = formatDescription(postMeta?.excerpt || getExcerpt(content));
  const pageTitle = formatPageTitle(title);
  const canonicalSlug = postMeta?.urlSlug || postMeta?.slug || urlSlugParam;
  const canonicalUrl = `${SITE_URL}/blog/${canonicalSlug}`;
  const articleSchema = buildSchemaGraph([
    buildOrganizationSchema(),
    buildWebSiteSchema(),
    buildArticleSchema({
      title,
      description,
      url: canonicalUrl,
      datePublished: postMeta?.date,
    }),
  ]);

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={description} />
      </Helmet>
      <JsonLd schema={articleSchema} />
      <div style={sharedStyles.article}>
        <div style={sharedStyles.backNav}>
          <Link to="/" style={linkStyle}>← Back to Home</Link>
          <span style={{ margin: '0 8px', color: darkMode ? '#666' : '#999' }}>|</span>
          <Link to="/blog" style={linkStyle}>← Back to Blog</Link>
        </div>

        <article>
          <header style={{ marginBottom: '40px' }}>
            <h1 style={sharedStyles.h1}>{title}</h1>
            {postMeta?.date && (
              <p style={{ fontSize: '16px', color: darkMode ? '#888' : '#999', marginTop: 0, marginBottom: 0 }}>
                {formatDate(postMeta.date)}
              </p>
            )}
          </header>
          <div style={{ fontSize: '18px', lineHeight: '1.9' }}>
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
              {body}
            </ReactMarkdown>
          </div>
        </article>
        <Footer />
      </div>
    </>
  );
}

export default BlogPost;
